// Typed fetch client for the Testbench host (same origin; in development Vite proxies to it).
// Every call sends the session cookie, parses RFC 9457 problems into ApiError, and validates the body
// with the endpoint's zod schema so a contract drift fails loudly in one place.

import { z } from 'zod'

export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  requiredRole?: string | null
  currentRole?: string | null
  [key: string]: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetails | null

  constructor(message: string, status: number, problem: ProblemDetails | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  get isNetwork(): boolean {
    return this.status === 0
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.problem?.detail ?? error.problem?.title ?? error.message
  return error instanceof Error ? error.message : String(error)
}

/** Builds the ApiError for a failed response; exported for tests. */
export async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetails | null = null
  try {
    problem = (await response.json()) as ProblemDetails
  } catch {
    /* not JSON */
  }
  return new ApiError(problem?.title ?? `HTTP ${response.status}`, response.status, problem)
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

const failureListeners = new Set<(path: string, error: ApiError) => void>()

/** Session handling observes failures from queries, mutations, and direct calls alike. */
export function subscribeApiFailures(listener: (path: string, error: ApiError) => void): () => void {
  failureListeners.add(listener)
  return () => failureListeners.delete(listener)
}

export async function apiFetch<T>(path: string, schema: z.ZodType<T>, method: Method = 'GET', body?: unknown): Promise<T> {
  const response = await send(path, method, body)
  if (response.status === 204) return schema.parse(undefined)
  return schema.parse(await response.json())
}

/** For calls whose success has no body worth reading (204 No Content). */
export async function apiSend(path: string, method: Method, body?: unknown): Promise<void> {
  await send(path, method, body)
}

async function send(path: string, method: Method, body?: unknown): Promise<Response> {
  let response: Response
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (error) {
    throw new ApiError(error instanceof Error ? error.message : 'Network error', 0)
  }
  if (!response.ok) {
    const error = await toApiError(response)
    for (const listener of failureListeners) listener(path, error)
    throw error
  }
  return response
}
